import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  updateDoc,
  doc,
  orderBy 
} from 'firebase/firestore';
import { db } from '../services/firebase';
import notificationService from '../services/notificationService';
import { toast } from 'react-toastify';
import './MyLessons.css';

const MyLessons = () => {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pending, confirmed, completed, cancelled

  useEffect(() => {
    if (currentUser && userProfile) {
      loadBookings();
    }
  }, [currentUser, userProfile, filter]);

  const loadBookings = async () => {
    try {
      setLoading(true);
      
      let q;
      
      // Öğretmen mi öğrenci mi?
      if (userProfile.role === 'teacher') {
        // Öğretmen: kendi derslerini görsün
        q = query(
          collection(db, 'bookings'),
          where('teacherId', '==', currentUser.uid),
          orderBy('dateTime', 'desc')
        );
      } else {
        // Öğrenci: kendi rezervasyonlarını görsün
        q = query(
          collection(db, 'bookings'),
          where('studentId', '==', currentUser.uid),
          orderBy('dateTime', 'desc')
        );
      }

      const snapshot = await getDocs(q);
      const bookingsList = [];
      
      snapshot.forEach((doc) => {
        bookingsList.push({ id: doc.id, ...doc.data() });
      });

      // Filtreleme uygula
      let filtered = bookingsList;
      if (filter !== 'all') {
        filtered = bookingsList.filter(b => b.status === filter);
      }

      setBookings(filtered);
      console.log(`✅ Loaded ${filtered.length} bookings for ${userProfile.role}`);
      
    } catch (error) {
      console.error('Load bookings error:', error);
      toast.error('Failed to load lessons');
    } finally {
      setLoading(false);
    }
  };

  // Öğretmen: Rezervasyonu onayla
  const handleConfirm = async (bookingId) => {
    try {
      const booking = bookings.find(b => b.id === bookingId);
      
      await updateDoc(doc(db, 'bookings', bookingId), {
        status: 'confirmed',
        confirmedAt: new Date().toISOString()
      });
      
      // Öğrenciye bildirim gönder
      try {
        await notificationService.notifyBookingConfirmed(booking.studentId, {
          id: bookingId,
          teacherName: booking.teacherName,
          dateTime: booking.dateTime
        });
        console.log('✅ Notification sent to student');
      } catch (notifError) {
        console.error('Notification error:', notifError);
      }
      
      toast.success('Lesson confirmed! 🎉');
      loadBookings();
    } catch (error) {
      console.error('Confirm error:', error);
      toast.error('Failed to confirm lesson');
    }
  };

  // Öğretmen: Rezervasyonu reddet
  const handleReject = async (bookingId) => {
    try {
      const booking = bookings.find(b => b.id === bookingId);
      
      await updateDoc(doc(db, 'bookings', bookingId), {
        status: 'cancelled',
        cancelledBy: 'teacher',
        cancelledAt: new Date().toISOString()
      });
      
      // Öğrenciye bildirim gönder
      try {
        await notificationService.notifyBookingCancelled(
          booking.studentId,
          {
            id: bookingId,
            dateTime: booking.dateTime
          },
          'teacher'
        );
      } catch (notifError) {
        console.error('Notification error:', notifError);
      }
      
      toast.info('Lesson rejected');
      loadBookings();
    } catch (error) {
      console.error('Reject error:', error);
      toast.error('Failed to reject lesson');
    }
  };

  // Öğrenci: Rezervasyonu iptal et
  const handleCancel = async (bookingId) => {
    if (!window.confirm('Are you sure you want to cancel this lesson?')) {
      return;
    }

    try {
      const booking = bookings.find(b => b.id === bookingId);
      
      await updateDoc(doc(db, 'bookings', bookingId), {
        status: 'cancelled',
        cancelledBy: 'student',
        cancelledAt: new Date().toISOString()
      });
      
      // Öğretmene bildirim gönder
      try {
        await notificationService.notifyBookingCancelled(
          booking.teacherId,
          {
            id: bookingId,
            dateTime: booking.dateTime
          },
          'student'
        );
      } catch (notifError) {
        console.error('Notification error:', notifError);
      }
      
      toast.info('Lesson cancelled');
      loadBookings();
    } catch (error) {
      console.error('Cancel error:', error);
      toast.error('Failed to cancel lesson');
    }
  };

  // Derse başla (gelecekte video odası)
  const handleStartLesson = (bookingId) => {
    navigate(`/lesson/${bookingId}`);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      weekday: 'short', 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatTime = (timeString) => {
    return timeString;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'Pending', class: 'status-pending', icon: '⏳' },
      confirmed: { label: 'Confirmed', class: 'status-confirmed', icon: '✅' },
      completed: { label: 'Completed', class: 'status-completed', icon: '🎓' },
      cancelled: { label: 'Cancelled', class: 'status-cancelled', icon: '❌' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`status-badge ${config.class}`}>
        {config.icon} {config.label}
      </span>
    );
  };

  const isPastLesson = (dateTime) => {
    return new Date(dateTime) < new Date();
  };

  const isUpcoming = (dateTime) => {
    const lessonDate = new Date(dateTime);
    const now = new Date();
    const hoursDiff = (lessonDate - now) / (1000 * 60 * 60);
    
    // DEVELOPMENT MODE: Her zaman true döner (test için)
    // Production'da bu satırı kaldırın
    return true;
    
    // PRODUCTION MODE: Sadece ders saatinden 2 saat önce aktif
    // return hoursDiff > 0 && hoursDiff <= 2;
  };

  if (loading) {
    return (
      <div className="my-lessons-page">
        <div className="container">
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading your lessons...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="my-lessons-page">
      <div className="container">
        {/* Header */}
        <div className="page-header">
          <div>
            <h1>
              {userProfile.role === 'teacher' ? 'My Teaching Schedule' : 'My Lessons'}
            </h1>
            <p className="page-subtitle">
              {userProfile.role === 'teacher' 
                ? 'Manage your upcoming lessons and student bookings'
                : 'View and manage your booked lessons'}
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="filters-bar">
          <button 
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All ({bookings.length})
          </button>
          <button 
            className={`filter-btn ${filter === 'pending' ? 'active' : ''}`}
            onClick={() => setFilter('pending')}
          >
            ⏳ Pending
          </button>
          <button 
            className={`filter-btn ${filter === 'confirmed' ? 'active' : ''}`}
            onClick={() => setFilter('confirmed')}
          >
            ✅ Confirmed
          </button>
          <button 
            className={`filter-btn ${filter === 'completed' ? 'active' : ''}`}
            onClick={() => setFilter('completed')}
          >
            🎓 Completed
          </button>
        </div>

        {/* Bookings List */}
        {bookings.length > 0 ? (
          <div className="bookings-list">
            {bookings.map((booking) => (
              <div 
                key={booking.id} 
                className={`booking-card ${isPastLesson(booking.dateTime) ? 'past-lesson' : ''}`}
              >
                {/* Card Header */}
                <div className="booking-header">
                  <div className="booking-main-info">
                    <div className="user-avatar">
                      {userProfile.role === 'teacher' 
                        ? booking.studentName?.charAt(0) || 'S'
                        : booking.teacherName?.charAt(0) || 'T'}
                    </div>
                    <div>
                      <h3 className="booking-title">
                        {userProfile.role === 'teacher' 
                          ? `Lesson with ${booking.studentName}`
                          : `Lesson with ${booking.teacherName}`}
                      </h3>
                      <p className="booking-subtitle">
                        {userProfile.role === 'teacher' && booking.studentEmail}
                      </p>
                    </div>
                  </div>
                  {getStatusBadge(booking.status)}
                </div>

                {/* Card Body */}
                <div className="booking-body">
                  <div className="booking-details">
                    <div className="detail-item">
                      <span className="detail-label">📅 Date:</span>
                      <span className="detail-value">{formatDate(booking.dateTime)}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">🕐 Time:</span>
                      <span className="detail-value">{formatTime(booking.time)}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">⏱️ Duration:</span>
                      <span className="detail-value">{booking.duration} minutes</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">💷 Price:</span>
                      <span className="detail-value">£{booking.price}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">📚 Type:</span>
                      <span className="detail-value">
                        {booking.lessonType === 'trial' ? 'Trial Lesson' : 'Regular Lesson'}
                      </span>
                    </div>
                  </div>

                  {/* Notes */}
                  {booking.notes && (
                    <div className="booking-notes">
                      <strong>Notes:</strong>
                      <p>{booking.notes}</p>
                    </div>
                  )}
                </div>

                {/* Card Footer - Actions */}
                <div className="booking-footer">
                  {/* Öğretmen görünümü */}
                  {userProfile.role === 'teacher' && (
                    <>
                      {booking.status === 'pending' && (
                        <div className="action-buttons">
                          <button 
                            onClick={() => handleConfirm(booking.id)}
                            className="btn btn-success"
                          >
                            ✅ Confirm Lesson
                          </button>
                          <button 
                            onClick={() => handleReject(booking.id)}
                            className="btn btn-danger"
                          >
                            ❌ Reject
                          </button>
                        </div>
                      )}
                      {booking.status === 'confirmed' && isUpcoming(booking.dateTime) && (
                        <button 
                          onClick={() => handleStartLesson(booking.id)}
                          className="btn btn-primary"
                        >
                          🎥 Start Lesson
                        </button>
                      )}
                    </>
                  )}

                  {/* Öğrenci görünümü */}
                  {userProfile.role === 'student' && (
                    <>
                      {booking.status === 'pending' && (
                        <button 
                          onClick={() => handleCancel(booking.id)}
                          className="btn btn-secondary"
                        >
                          Cancel Request
                        </button>
                      )}
                      {booking.status === 'confirmed' && isUpcoming(booking.dateTime) && (
                        <button 
                          onClick={() => handleStartLesson(booking.id)}
                          className="btn btn-primary"
                        >
                          🎥 Join Lesson
                        </button>
                      )}
                      {booking.status === 'confirmed' && !isPastLesson(booking.dateTime) && (
                        <button 
                          onClick={() => handleCancel(booking.id)}
                          className="btn btn-secondary"
                          style={{ marginLeft: '10px' }}
                        >
                          Cancel Lesson
                        </button>
                      )}
                    </>
                  )}

                  {/* Booked date info */}
                  <p className="booking-created">
                    Booked {new Date(booking.createdAt?.seconds * 1000 || booking.createdAt).toLocaleDateString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-icon">📚</div>
            <h2>No lessons yet</h2>
            <p>
              {userProfile.role === 'teacher' 
                ? "You don't have any bookings yet. Students will see your profile and book lessons."
                : "You haven't booked any lessons yet. Find a teacher and start learning!"}
            </p>
            {userProfile.role === 'student' && (
              <button 
                onClick={() => navigate('/find-teachers')}
                className="btn btn-primary"
                style={{ marginTop: '20px' }}
              >
                Find Teachers
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyLessons;